import { Edge, Node, Position } from '@xyflow/react';
import { ApprovalLevelViewRowValues } from '../../types';
import { BRANCH_NODE_HEIGHT } from '../../constants';

interface MeasuredNode extends Node {
  measured?: {
    width: number;
    height: number;
  };
}

type FlowGraph = {
  nodes: MeasuredNode[];
  edges: Edge[];
};

function areAllObjectValuesNull(
  obj: Record<string, any>,
  excludeKeys: string[] = []
): boolean {
  return Object.values(obj).every((value) => value === null);
}

const LEVEL_NODE_WIDTH = 200;
const LEVEL_NODE_HEIGHT = 100;
const BRANCH_NODE_WIDTH = 150;

const PADDING_Y_BETWEEN_LEVELS = 120;
const PADDING_X_LEVEL_TO_BRANCH = 150;
const PADDING_Y_BETWEEN_BRANCHES = 100;

export const convertToApprovalLevelNodes = (
  approvalLevels: ApprovalLevelViewRowValues[],
  layout: 'horizontal' | 'vertical' = 'vertical',
  isFlowUpdatable?: boolean,
  isFlowDeletable?: boolean
): FlowGraph => {
  const nodes: MeasuredNode[] = [];
  const edges: Edge[] = [];

  let currentYOffset = 0;
  const startNodeHeight = 50;

  nodes.push({
    id: 'node-start',
    type: 'TitleNode',
    data: { label: 'Approval levels' },
    position: { x: 0, y: currentYOffset },
    sourcePosition: Position.Bottom,
  });

  currentYOffset += startNodeHeight + PADDING_Y_BETWEEN_LEVELS;

  approvalLevels.forEach((level, levelIndex) => {
    const levelNodeId = `${levelIndex}-level`;
    const isPlaceholderLevel = level.name === null;
    const approvalBranches = level.approvalBranches || [];

    const currentLevelNode: MeasuredNode = {
      id: levelNodeId,
      type: isPlaceholderLevel ? 'AddLevelNode' : 'LevelNode',
      data: {
        ...level,
        levelIndex,
        isFlowUpdatable: isFlowUpdatable ?? true,
        isFlowDeletable: isFlowDeletable ?? true,
      },
      position: { x: 0, y: currentYOffset },
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
      measured: { width: LEVEL_NODE_WIDTH, height: LEVEL_NODE_HEIGHT },
    };
    nodes.push(currentLevelNode);

    if (levelIndex === 0) {
      edges.push({
        id: `edge-start-to-${levelNodeId}`,
        source: 'node-start',
        target: levelNodeId,
        sourceHandle: 'a',
        animated: true,
      });
    } else {
      const sourceId = `${levelIndex - 1}-level`;
      edges.push({
        id: `edge-${sourceId}-to-${levelNodeId}`,
        source: sourceId,
        target: levelNodeId,
        targetHandle: isPlaceholderLevel ? undefined : 'level-edge',
        sourceHandle: 'edge-source',
        type: isPlaceholderLevel ? undefined : 'approver-level-custom-edge',
        animated: true,
      });
    }

    let maxBranchYForCurrentLevel =
      currentLevelNode.position.y + LEVEL_NODE_HEIGHT;
    let branchCurrentY = currentLevelNode.position.y;

    if (!isPlaceholderLevel) {
      approvalBranches.forEach((approver, approverIndex) => {
        const approverId = `${levelIndex}-level-${approverIndex}`;
        const isPlaceholderBranch = areAllObjectValuesNull(approver);

        const branchNode: MeasuredNode = {
          id: approverId,
          type: isPlaceholderBranch
            ? 'AddApprovalBranchNode'
            : 'ApprovalBranchNode',
          data: {
            ...approver,
            levelIndex,
            levelName: level.name,
            approverBranchIndex: approverIndex,
            isFlowUpdatable: isFlowUpdatable,
            isFlowDeletable: isFlowDeletable,
          },
          position: {
            x:
              currentLevelNode.position.x +
              LEVEL_NODE_WIDTH +
              PADDING_X_LEVEL_TO_BRANCH,
            y: branchCurrentY,
          },
          targetPosition: Position.Left,
          sourcePosition: Position.Bottom,
          measured: { width: BRANCH_NODE_WIDTH, height: BRANCH_NODE_HEIGHT },
        };
        nodes.push(branchNode);

        if (approverIndex === 0) {
          edges.push({
            id: `branch-edge-${levelNodeId}-to-${approverId}`,
            source: levelNodeId,
            target: approverId,
            sourceHandle: 'branch-level-edge',
            type: 'step',
            animated: true,
          });
        } else {
          const prevBranchId = `${levelIndex}-level-${approverIndex - 1}`;
          edges.push({
            id: `branch-edge-${prevBranchId}-to-${approverId}`,
            source: prevBranchId,
            target: approverId,
            type: 'step',
            animated: true,
          });
        }

        branchCurrentY += BRANCH_NODE_HEIGHT + PADDING_Y_BETWEEN_BRANCHES;
        maxBranchYForCurrentLevel = Math.max(
          maxBranchYForCurrentLevel,
          branchNode.position.y + BRANCH_NODE_HEIGHT
        );
      });
    }

    currentYOffset =
      Math.max(
        currentLevelNode.position.y + LEVEL_NODE_HEIGHT,
        maxBranchYForCurrentLevel
      ) + PADDING_Y_BETWEEN_LEVELS;
  });

  const lastLevel = approvalLevels[approvalLevels.length - 1];
  if (lastLevel && lastLevel.name !== null && isFlowUpdatable) {
    const addLevelNodeId = `${approvalLevels.length}-level`;
    nodes.push({
      id: addLevelNodeId,
      type: 'AddLevelNode',
      data: { label: 'Add Level' },
      position: { x: 0, y: currentYOffset },
      targetPosition: Position.Top,
      sourcePosition: Position.Bottom,
    });

    edges.push({
      id: `edge-${approvalLevels.length - 1}-level-to-${addLevelNodeId}`,
      source: `${approvalLevels.length - 1}-level`,
      target: addLevelNodeId,
      sourceHandle: 'edge-source',
      animated: true,
    });
  }

  return { nodes, edges };
};
